import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, docsApi, imagesApi, OllamaChatMessage } from '../../services/api';
import { useConfigStore } from '../../store/configStore';
import styles from './MarkdownEditor.module.css';
import { DocumentInfo } from './DocumentInfo';
import { DocumentEditor } from './DocumentEditor';
import { DocumentPreview } from './DocumentPreview';
import { AiChatInterface } from './AiChatInterface';

interface MarkdownEditorProps {
  documentId?: number;
  initialTitle?: string;
  initialContent?: string;
  initialFolderPath?: string;
  initialIsPrivate?: boolean;
  initialTags?: string[];
  mode: 'create' | 'edit';
}

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  documentId,
  initialTitle = '',
  initialContent = '',
  initialFolderPath = '/',
  initialIsPrivate = false,
  initialTags = [],
  mode
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [folderPath, setFolderPath] = useState(initialFolderPath);
  const [isPrivate, setIsPrivate] = useState(initialIsPrivate);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveToast, setSaveToast] = useState<'saving' | 'success' | null>(null);
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteSearch, setAutocompleteSearch] = useState('');
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
  const [autocompleteSelectedIndex, setAutocompleteSelectedIndex] = useState(0);
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [wikiLinkStart, setWikiLinkStart] = useState(-1);
  

  const [showAiChat, setShowAiChat] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState<OllamaChatMessage[]>([]);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  
  const { ollama } = useConfigStore();
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        const response = await docsApi.getAll();
        setAllDocuments(response.data);
      } catch (err) {
        console.error('Failed to load documents for autocomplete:', err);
      }
    };
    loadDocuments();
  }, []);

  useEffect(() => {
    if (mode === 'create' && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [mode]);

  useEffect(() => {
    const handleEditorSave = () => {
      if (!saving) {
        handleSave();
      }
    };

    document.addEventListener('editorSave', handleEditorSave);
    
    return () => {
      document.removeEventListener('editorSave', handleEditorSave);
    };
  }, [saving, title, content, folderPath, isPrivate, tags, mode, documentId]);

  useEffect(() => {
    if (!autocompleteSearch) {
      setFilteredDocuments(allDocuments);
    } else {
      const searchLower = autocompleteSearch.toLowerCase();
      const filtered = allDocuments.filter(doc =>
        doc.title.toLowerCase().includes(searchLower)
      );
      setFilteredDocuments(filtered);
    }
    setAutocompleteSelectedIndex(0);
  }, [autocompleteSearch, allDocuments]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setSaveToast('saving');
    setError('');

    try {
      if (mode === 'create') {
        const response = await docsApi.create({
          title,
          content,
          folder_path: folderPath,
          is_private: isPrivate,
          tags
        });
        setSaveToast('success');
        setTimeout(() => setSaveToast(null), 2000);
        setTimeout(() => navigate(`/docs/${response.data.slug}`), 500);
      } else if (mode === 'edit' && documentId) {
        const response = await docsApi.update(documentId, {
          title,
          content,
          folder_path: folderPath,
          is_private: isPrivate,
          tags
        });
        
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          for (const cacheName of cacheNames) {
            if (cacheName.includes('api-cache')) {
              const cache = await caches.open(cacheName);
              await cache.delete(`/api/docs/${response.data.slug}`);
              await cache.delete(`/api/docs`);
            }
          }
        }
        
        setSaveToast('success');
        setTimeout(() => setSaveToast(null), 2000);
        setTimeout(() => navigate(`/docs/${response.data.slug}`), 500);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save document');
      setSaveToast(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!documentId) return;
    
    if (!confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await docsApi.delete(documentId);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete document');
    }
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleImageUpload = async (file: File) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a valid image (jpg, png, gif, or webp).');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size exceeds 10MB limit.');
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      const imageInfo = await imagesApi.upload(file);
      
      const filename = imageInfo.filename.replace(/^\d+-/, '');
      const altText = filename.replace(/\.[^.]+$/, '');
      
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        const cursorPosition = textarea.selectionStart;
        const beforeCursor = content.substring(0, cursorPosition);
        const afterCursor = content.substring(cursorPosition);
        const markdownImage = `![${altText}](${imageInfo.url})`;
        
        const newContent = beforeCursor + markdownImage + afterCursor;
        setContent(newContent);
        
        setTimeout(() => {
          const newCursorPos = cursorPosition + markdownImage.length;
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    } catch (err: any) {
      console.error('Image upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = Array.from(e.dataTransfer.files);
    
    const imageFile = files.find(file => file.type.startsWith('image/'));
    if (imageFile) {
      await handleImageUpload(imageFile);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        
        const file = items[i].getAsFile();
        if (file) {
          await handleImageUpload(file);
        }
        return;
      }
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    setContent(newContent);

    const textBeforeCursor = newContent.substring(0, cursorPosition);
    const lastDoubleBracketIndex = textBeforeCursor.lastIndexOf('[[');
    
    if (lastDoubleBracketIndex !== -1) {
      const textAfterBracket = textBeforeCursor.substring(lastDoubleBracketIndex + 2);
      
      if (!textAfterBracket.includes(']]')) {
        setWikiLinkStart(lastDoubleBracketIndex);
        setAutocompleteSearch(textAfterBracket);
        setShowAutocomplete(true);
        
        if (textareaRef.current) {
          const textBeforeLink = newContent.substring(0, lastDoubleBracketIndex);
          const lines = textBeforeLink.split('\n');
          const currentLine = lines.length;
          const lineHeight = 20;
          const top = currentLine * lineHeight + 40;
          const left = 20;
          
          setAutocompletePosition({ top, left });
        }
      } else {
        setShowAutocomplete(false);
      }
    } else {
      setShowAutocomplete(false);
    }
  };

  const handleDocumentSelect = (doc: Document) => {
    if (wikiLinkStart === -1 || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    
    const beforeLink = content.substring(0, wikiLinkStart);
    const afterCursor = content.substring(cursorPosition);
    const markdownLink = `[${doc.title}](/docs/${doc.slug})`;
    
    const newContent = beforeLink + markdownLink + afterCursor;
    setContent(newContent);
    
    setShowAutocomplete(false);
    setAutocompleteSearch('');
    setWikiLinkStart(-1);
    
    setTimeout(() => {
      const newCursorPos = wikiLinkStart + markdownLink.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showAutocomplete) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAutocompleteSelectedIndex((prev) => 
        Math.min(prev + 1, filteredDocuments.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAutocompleteSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (filteredDocuments[autocompleteSelectedIndex]) {
        e.preventDefault();
        handleDocumentSelect(filteredDocuments[autocompleteSelectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowAutocomplete(false);
    }
  };

  return (
    <div className={styles.editorWrapper}>
      <DocumentInfo
        title={title}
        setTitle={setTitle}
        folderPath={folderPath}
        setFolderPath={setFolderPath}
        isPrivate={isPrivate}
        setIsPrivate={setIsPrivate}
        tags={tags}
        tagInput={tagInput}
        setTagInput={setTagInput}
        handleAddTag={handleAddTag}
        handleRemoveTag={handleRemoveTag}
        folderInputRef={folderInputRef}
        titleInputRef={titleInputRef}
        error={error}
      />

      { !showAiChat ?
        <div className={styles.editorContainer}>
          <DocumentEditor
            content={content}
            uploadingImage={uploadingImage}
            isDraggingOver={isDraggingOver}
            textareaRef={textareaRef}
            handleContentChange={handleContentChange}
            handleKeyDown={handleKeyDown}
            handleDragEnter={handleDragEnter}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handlePaste={handlePaste}
            showAutocomplete={showAutocomplete}
            filteredDocuments={filteredDocuments}
            autocompleteSelectedIndex={autocompleteSelectedIndex}
            autocompletePosition={autocompletePosition}
            handleDocumentSelect={handleDocumentSelect}
          />
          <DocumentPreview content={content} />
        </div>
      :
        <div className={styles.editorContainer}>
          <DocumentPreview content={content} />
          <AiChatInterface
            showAiChat={showAiChat}
            ollamaEnabled={ollama.enabled}
            aiInput={aiInput}
            setAiInput={setAiInput}
            aiMessages={aiMessages}
            setAiMessages={setAiMessages}
            isAiGenerating={isAiGenerating}
            setIsAiGenerating={setIsAiGenerating}
            setError={setError}
            setShowAiChat={setShowAiChat}
            setContent={setContent}
            mode={mode}
            documentId={documentId}
            title={title}
            content={content}
            folderPath={folderPath}
            isPrivate={isPrivate}
            tags={tags}
            navigation={navigate}
          />
        </div>
      }

      <div className={styles.formActions}>
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : mode === 'create' ? 'Create Document' : 'Save Changes'}
        </button>
        <button
          className={`${styles.btn} ${styles.btnSecondary}`}
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
        {mode === 'edit' && (
          <button
            className={`${styles.btn} ${styles.btnDanger}`}
            onClick={handleDelete}
          >
            Delete
          </button>
        )}
        {ollama.enabled && (
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => {
              setShowAiChat(!showAiChat);
              setAiMessages([]);
              setAiInput('');
            }}
            disabled={saving || isAiGenerating}
          >
            ✨ {showAiChat ? 'Close AI' : 'AI'}
          </button>
        )}
      </div>
      
      {saveToast && (
        <div className={`${styles.saveToast} ${saveToast === 'success' ? styles.saveToastSuccess : ''}`}>
          {saveToast === 'saving' ? '💾 Saving...' : '✓ Saved!'}
        </div>
      )}
    </div>
  );
};
